import { Link, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect } from 'react';
import './ThankYou.css';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import Canonical from '../../seo/Canonical';

type CallBookingState = {
  email?: string;
  preferredDateLabel?: string;
  preferredTimeUK?: string;
};

const ThankYou = () => {
  const location = useLocation();
  const callBooking = (location.state as { callBooking?: CallBookingState } | null)?.callBooking;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Log thank you page view for analytics (optional)
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_path: '/thank-you',
        page_title: 'Thank You - Pick Pack Pro'
      });
    }
  }, []);

  const quickLinks = [
    { path: '/', title: 'Home', icon: '🏠' },
    { path: '/shopify-fulfillment-services', title: 'Shopify Fulfilment', icon: '📦' },
    { path: '/amazon-fba-prep-services', title: 'Amazon FBA Prep', icon: '📦' },
    { path: '/about-our-fulfillment-company', title: 'About Us', icon: 'ℹ️' },
    { path: '/fulfillment-frequently-asked-questions', title: 'FAQs', icon: '❓' },
    { path: '/fulfillment-blog', title: 'Blog', icon: '📝' }
  ];

  return (
    <div className="thank-you-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      {/* Hero Section */}
      <section className="thank-you-hero">
        <div className="thank-you-hero-container">
          <div className="thank-you-content">
            {/* Success Icon */}
            <div className="thank-you-icon">
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="60" cy="60" r="55" fill="url(#successGradient)" opacity="0.2"/>
                <circle cx="60" cy="60" r="50" stroke="url(#successGradient)" strokeWidth="4" fill="none"/>
                <path d="M35 60L50 75L85 40" stroke="url(#successGradient)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Main Content */}
            <div className="thank-you-text">
              <h1 className="thank-you-title">
                {callBooking ? 'Appointment Request Received!' : 'Thank You!'}
              </h1>
              <p className="thank-you-description">
                {callBooking ? (
                  <>
                    {callBooking.preferredDateLabel && callBooking.preferredTimeUK ? (
                      <>
                        Your preferred call is{' '}
                        <strong>
                          {callBooking.preferredDateLabel} at {callBooking.preferredTimeUK} (UK)
                        </strong>
                        .{' '}
                      </>
                    ) : null}
                    {callBooking.email ? (
                      <>
                        A confirmation has been sent to <strong>{callBooking.email}</strong>. Please
                        check your inbox (and spam folder). Our team will confirm the final slot
                        shortly.
                      </>
                    ) : (
                      <>
                        We&apos;ve received your booking. Our team will confirm your consultation
                        slot by email shortly.
                      </>
                    )}
                  </>
                ) : (
                  <>
                    We&apos;ve received your message and our team will get back to you within 24
                    hours. We&apos;re excited to help you with your fulfillment needs!
                  </>
                )}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="thank-you-actions">
              <Link to="/" className="thank-you-btn thank-you-btn-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                Go to Homepage
              </Link>
              <Link to="/contact-fulfillment-services" className="thank-you-btn thank-you-btn-secondary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Contact Us Again
              </Link>
            </div>
          </div>

          {/* Illustration/Icon */}
          <div className="thank-you-illustration">
            <svg width="300" height="300" viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="150" cy="150" r="140" fill="url(#gradient2)" opacity="0.1"/>
              <path d="M100 150L130 180L200 110" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
              <circle cx="150" cy="100" r="30" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.3"/>
              <path d="M120 200L150 230L180 200" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
              <defs>
                <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#28479e" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="thank-you-links-section">
        <div className="thank-you-links-container">
          <h2 className="thank-you-links-title">Explore Our Services</h2>
          <p className="thank-you-links-subtitle">While you wait, check out what we offer:</p>
          
          <div className="thank-you-links-grid">
            {quickLinks.map((link) => (
              <Link key={link.path} to={link.path} className="thank-you-link-card">
                <div className="thank-you-link-icon">{link.icon}</div>
                <h3 className="thank-you-link-title">{link.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Next Steps Section */}
      <section className="thank-you-next-steps-section">
        <div className="thank-you-next-steps-container">
          <div className="thank-you-next-steps-content">
            <h2 className="thank-you-next-steps-title">What Happens Next?</h2>
            <div className="thank-you-steps-grid">
              <div className="thank-you-step">
                <div className="thank-you-step-number">1</div>
                <h3 className="thank-you-step-title">We Review Your Request</h3>
                <p className="thank-you-step-text">
                  Our team will carefully review your requirements and prepare a customized solution.
                </p>
              </div>
              <div className="thank-you-step">
                <div className="thank-you-step-number">2</div>
                <h3 className="thank-you-step-title">We Contact You</h3>
                <p className="thank-you-step-text">
                  Within 24 hours, one of our fulfillment experts will reach out to discuss your needs.
                </p>
              </div>
              <div className="thank-you-step">
                <div className="thank-you-step-number">3</div>
                <h3 className="thank-you-step-title">We Get Started</h3>
                <p className="thank-you-step-text">
                  Once we understand your requirements, we'll provide a tailored quote and get you started.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default ThankYou;
